import dynamic from "next/dynamic";

import { useContext, useEffect, useMemo, useState } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Tooltip from "react-bootstrap/Tooltip";
import type { Placement } from "react-bootstrap/types";

import { twMerge } from "tailwind-merge";

import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons/faLinkedinIn";
import { faTwitter } from "@fortawesome/free-brands-svg-icons/faTwitter";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { faClipboard } from "@fortawesome/free-regular-svg-icons/faClipboard";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons/faEnvelope";
import { faEnvelopeOpen } from "@fortawesome/free-regular-svg-icons/faEnvelopeOpen";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons/faShareAlt";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { NotificationContext } from "components/Notification/NotificationContext";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import { roboto } from "utils/fonts";

import style from "./style.module.scss";

const NewPrivateMessageModal = dynamic(
  () => import("components/NewPrivateMessageModal"),
  { ssr: false }
);

type ShareButtonProps = {
  shareKey?: any;
  entity?: any;
  profile?: any;
  collection?: any;
  options: any;
  noBorder?: boolean;
  columnView?: boolean;
  children?: React.ReactNode;
  className?: string;
  activeClassName?: string;
  containerClassName?: string;
  noTooltip?: boolean;
  tooltipPlacement?: Placement;
  type?: "default" | "pill-button" | "icon";
  showIcon?: boolean;
  iconSize?: string;
  iconClassName?: string;
  optionPlacement?: Placement;
  delay?: number;
};

const icon = faShareAlt;

const ShareButton = ({
  shareKey,
  options,
  noBorder,
  columnView,
  children,
  className,
  containerClassName,
  profile,
  noTooltip,
  tooltipPlacement,
  type = "default",
  showIcon = true,
  iconSize,
  activeClassName,
  iconClassName,
  optionPlacement,
  delay,
}: ShareButtonProps) => {
  const { authenticated, account: loggedInAccount } = useAuthentication();
  const { toggleModal } = useSignInSignUp();

  const { setNotification } = useContext(NotificationContext);

  const [showToolTip, setShowToolTip] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [openedSharedKey, setOpenedShareKey] = useGlobalState(
    "isSharePanelOpened",
    undefined
  );
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);

  const media = useMediaQuery();

  const shareOptions = useMemo(
    () => [
      ...(options.email
        ? [
            {
              id: "email",
              icon: faEnvelopeOpen,
              color: "#1f1f20",
              tooltipText: "Share via Email",
              link: options.email.link,
            },
          ]
        : []),
      ...(options.clipboard
        ? [
            {
              id: "clipboard",
              icon: faClipboard,
              color: "#1f1f20",
              tooltipText: options.clipboard.tooltipText || "Copy Link",
              link: "",
              message: options.clipboard.message,
            },
          ]
        : []),
      ...(loggedInAccount?.canUsePrivateMessages
        ? [
            {
              id: "message",
              icon: faEnvelope,
              color: "#24a9a7",
              tooltipText: "Send private Message",
              link: "",
            },
          ]
        : []),
      ...(options.whatsapp
        ? [
            {
              id: "whatsapp",
              icon: faWhatsapp,
              color: "#25d366",
              tooltipText: "Share via Whatsapp",
              link: options.whatsapp.link,
            },
          ]
        : []),
      ...(options.twitter
        ? [
            {
              id: "twitter",
              icon: faTwitter,
              color: "#1da1f2",
              tooltipText: "Share via Twitter",
              link: options.twitter.link,
            },
          ]
        : []),
      ...(options.linkedIn
        ? [
            {
              id: "linkedin",
              icon: faLinkedinIn,
              color: "#007bb6",
              tooltipText: "Share via LinkedIn",
              link: options.linkedIn.link,
            },
          ]
        : []),
    ],
    [
      loggedInAccount?.canUsePrivateMessages,
      options.clipboard,
      options.email,
      options.linkedIn,
      options.twitter,
      options.whatsapp,
    ]
  );

  // button action options
  const onOptionClick = (e: React.MouseEvent, option: any) => {
    e.stopPropagation();
    e.preventDefault();

    setShowPanel(false);

    if (option.id === "clipboard") {
      // copy URL to clipboard
      navigator.clipboard.writeText(option.message);
      // display toast notification
      setNotification("Copied to clipboard successfully.", "success");
    } else if (option.id === "message") {
      if (authenticated) {
        setShowNewMessageModal(true);
      } else {
        toggleModal("sign-in");
      }
    } else if (option.link) {
      window.open(option.link, "_blank");
    }
  };

  // render all options in share panel
  const renderOptions = (option: any, key: any) => {
    return (
      <OverlayTrigger
        key={key}
        placement="auto"
        overlay={
          <Tooltip className={style.tooltip}>{option.tooltipText}</Tooltip>
        }
      >
        <li onClick={(e) => onOptionClick(e, option)}>
          <span className={style.share_option_container}>
            {option.link !== "" ? (
              <a
                target="_blank"
                rel="noreferrer"
                href={option.link}
                title={option.tooltipText}
              >
                <FontAwesomeIcon
                  icon={option.icon}
                  className={style.share_option_icon}
                  style={{ color: option.color }}
                />
              </a>
            ) : (
              <button className={style.share_option_btn}>
                <FontAwesomeIcon
                  icon={option.icon}
                  className={style.share_option_icon}
                  style={{ color: option.color }}
                />
              </button>
            )}
          </span>
        </li>
      </OverlayTrigger>
    );
  };

  // render share panel
  const renderSharePanel = () => {
    return (
      <OverlayTrigger
        placement={
          optionPlacement
            ? optionPlacement
            : columnView
            ? "left-start"
            : media.mobile
            ? "bottom"
            : "left"
        }
        trigger={"click"}
        rootClose
        show={showPanel && (shareKey ? shareKey === openedSharedKey : true)}
        onExit={() => {
          setShowToolTip(false);
          setShowPanel(false);
        }}
        onToggle={(data) => {
          if (!data) {
            setShowPanel(data);
          }
        }}
        overlay={
          <Popover
            className={twMerge(roboto.variable, "font-roboto", style.popover)}
          >
            <Popover.Body className={style.share_panel_popover_body}>
              <div className={style.popover_body_container}>
                <ul className={twMerge(columnView && "!flex-col-reverse")}>
                  {shareOptions.map(renderOptions)}
                </ul>
              </div>
            </Popover.Body>
          </Popover>
        }
      >
        {type === "icon" ? (
          <button
            className={twMerge(
              "bg-transparent outline-none border-none",
              "flex flex-col items-center",
              "text-[13px] text-center",
              "text-white"
            )}
            aria-label="share"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              setShowPanel(!showPanel);
            }}
          >
            <FontAwesomeIcon icon={faShareAlt} className="text-[24px]" />
            Share
          </button>
        ) : type === "pill-button" ? (
          <button
            className={twMerge(
              "bg-white md:hover:bg-smart-teal text-smart-teal md:hover:text-white",
              "rounded-full border border-solid md:border-smart-teal",
              showPanel
                ? "bg-smart-teal border-smart-teal text-white"
                : "bg-white border-white",
              "text-[13px] md:text-[14px] leading-[1] md:leading-normal",
              "px-[1.22em] py-[0.72em] md:px-[19px] md:py-[6px]",
              "flex"
            )}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              setShowPanel(!showPanel);
            }}
          >
            {children}
          </button>
        ) : (
          <div
            className={twMerge(
              containerClassName || style.button_icon_container,
              children && style.button_with_children
            )}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              setShowPanel(!showPanel);
            }}
          >
            {showIcon && (
              <FontAwesomeIcon
                icon={showPanel ? faTimes : icon}
                width={iconSize}
                height={iconSize}
                className={iconClassName}
              />
            )}
            <span className={style.children}>{children}</span>
          </div>
        )}
      </OverlayTrigger>
    );
  };

  useEffect(() => {
    if (showPanel && shareKey) {
      setOpenedShareKey(shareKey);
    }
  }, [setOpenedShareKey, shareKey, showPanel]);

  return (
    <>
      {noTooltip ? (
        <div
          className={twMerge(
            className,
            noBorder || type !== "default"
              ? style.noBorderContainer
              : style.container,
            media.mobile && style.mobileLayout,
            showPanel &&
              (shareKey ? shareKey === openedSharedKey : true) &&
              activeClassName
          )}
        >
          {renderSharePanel()}
        </div>
      ) : (
        <OverlayTrigger
          placement={tooltipPlacement || (media.mobile ? "bottom" : "left")}
          show={showToolTip && !showPanel}
          overlay={<Tooltip className={style.toolTip}>Share</Tooltip>}
          delay={delay}
        >
          <div
            className={twMerge(
              className,
              noBorder || type !== "default"
                ? style.noBorderContainer
                : style.container,
              media.mobile && style.mobileLayout
            )}
            onMouseEnter={() => {
              setShowToolTip(true);
            }}
            onMouseLeave={() => {
              setShowToolTip(false);
            }}
          >
            {renderSharePanel()}
          </div>
        </OverlayTrigger>
      )}
      {showNewMessageModal && (
        <NewPrivateMessageModal
          preselectRecipients={profile ? [profile] : []}
          defaultMessage={options.privateMessage?.message}
          onClose={() => setShowNewMessageModal(false)}
        />
      )}
    </>
  );
};

export default ShareButton;
